import { useEffect, useRef, useState } from "react";
import type { TransactionDetailProps } from "./TransactionDetail";
import type { ReceiptDraft, SavedReceipt, TransactionReceipts } from "../../types/receipt";
import type { EditJournal } from "./edit-save";
import { normalizeLedgerFields } from "../../lib/ledger-field-compat";
import { api } from "../../lib/api";
import { loadPriceList } from "../../lib/prices";
import { isExpense } from "../../lib/finance";
import { transactionDraft } from "./transaction-draft";

type Props = Pick<TransactionDetailProps, "detail" | "data" | "setDetailBusy" | "setDetailPrices" | "editKey" | "editPriceSnapshot" | "persistEdit" | "setDetailError" | "setEditingDetail">;

export function useStartTransactionEdit({ detail, data, setDetailBusy, setDetailPrices, editKey, editPriceSnapshot, persistEdit, setDetailError, setEditingDetail }: Props) {
  const [clearing, setClearing] = useState(false);
  const [receipts, setReceipts] = useState<SavedReceipt[]>([]);
  const [notice, setNotice] = useState("");
  const active = useRef(true), currentId = useRef(detail.id);
  currentId.current = detail.id;
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  async function startEdit(clear = false, requested?: ReceiptDraft) {
    setDetailBusy(true);
    try {
      const { prepareReceiptEdit } = await import("./receipt-edit-draft");
      const stored = localStorage.getItem(editKey(detail.id));
      const journal = stored ? normalizeLedgerFields(JSON.parse(stored)) as EditJournal : null;
      const [prices, evidence] = await Promise.all([loadPriceList(true),
        isExpense(detail) && !journal?.started
          ? api<TransactionReceipts>(`receipts?transactionId=${encodeURIComponent(detail.id)}`) : undefined]);
      if (!active.current || currentId.current !== detail.id) return;
      const linked = prices.filter(price => price.transactionId === detail.id);
      editPriceSnapshot.current = journal?.originalPrices || linked;
      setDetailPrices(linked);
      const next: EditJournal = journal || { transaction: detail, originalPrices: linked,
        expense: { ...transactionDraft(detail, linked), ...(clear ? { pending: false } : {}), ...(detail.needsReview ? { needsReview: false } : {}) },
        ...(clear ? { clearing: true } : {}),
        started: false, transactionSaved: false, completed: [] };
      const saved = evidence?.receipts || (evidence?.receipt || evidence?.images?.length ? [evidence] : []);
      setReceipts(saved);
      const currency = data?.accounts.find(account => account.id === next.expense.accountId)?.currency || detail.currency;
      const prepared = prepareReceiptEdit(next, saved, currency, requested && !journal ? requested : undefined);
      setNotice(prepared.notice);
      persistEdit(prepared.journal);
      setDetailError(requested && journal ? "Your earlier transaction draft was restored. Finish or discard it before importing receipt details." : "");
      setClearing(next.clearing === true);
      setEditingDetail(true);
    } catch (error) { setDetailError(error instanceof Error ? error.message : "Transaction details could not load. Try again."); }
    finally { setDetailBusy(false); }
  }
  return { startEdit, clearing, receipts, notice };
}
